import { Box, Typography, keyframes } from '@mui/material'
import { useEffect, useState } from 'react'
import { RxInfoCircled } from 'react-icons/rx'
import { tss } from 'tss-react/mui'
import { useTypedTranslation } from '../common/hooks/useTypedTranslation'
import { Chip } from './ui/atoms/chip/Chip'

const pulseAnimation = keyframes`
  0% {
    transform: scale(0.9);
    opacity: 1;
  }
  70% {
    transform: scale(2.0);
    opacity: 0;
  }
  100% {
    transform: scale(2.8);
    opacity: 0;
  }
`

const useStyles = tss.create(({ theme }) => ({
  root: {
    backgroundColor: theme.palette.common.doss.gray[10],
    color: theme.palette.common.doss.gray[5],
    overflow: 'hidden',
    transition: 'height ease-in-out 0.3s',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'row',
    gap: theme.spacing(2),
    paddingBlock: '0',
    paddingInline: '8px',
  },
  textGroup: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing(1),
  },
  dotRadidate: {
    backgroundColor: theme.palette.common.doss.green[8],
    width: 8,
    height: 8,
    borderRadius: '50%',
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: 8,
      height: 8,
      borderRadius: '50%',
      animation: `${pulseAnimation} 2s infinite ease-in-out`,
      border: `2px solid ${theme.palette.common.doss.green[8]}`,
    },
  },
}))

const fetchVersion = async (version = '') => {
  try {
    const response = await fetch('/version.json')
    const data = await response.json()
    return data.version as string
  } catch {
    return version
  }
}

/**
 * Renders a banner that notifies the user that a new version of the application is available.
 */
export const VersioningBanner = () => {
  const { classes } = useStyles()
  const [version, setVersion] = useState('')
  const [showBanner, setShowBanner] = useState(false)
  const { t } = useTypedTranslation()

  useEffect(() => {
    fetchVersion().then((v) => setVersion(v))
  }, [])

  useEffect(() => {
    const interval = setInterval(async () => {
      const currentVersion = await fetchVersion(version)
      if (currentVersion !== version) {
        setShowBanner(true)
      }
    }, 60_000)
    return () => clearInterval(interval)
  }, [version])

  return (
    <Box
      className={classes.root}
      sx={{ height: showBanner ? '42px' : '0px', minHeight: showBanner ? '42px' : '0px' }}
    >
      <Box className={classes.textGroup}>
        <RxInfoCircled />
        <Typography variant="body1">{t('versioning_banner.title')}</Typography>
      </Box>
      <Chip
        clickable
        color="green"
        component="button"
        icon={<Box className={classes.dotRadidate} />}
        label={t('common.actions.update')}
        onClick={() => window.location.reload()}
        size="medium"
        variant="solid"
      ></Chip>
    </Box>
  )
}
